import { useState } from 'react';
import { Alert, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RedeemIcon from '@mui/icons-material/Redeem';
import { useAdminSolicitarAcessoMutation } from '../../api';

/** Como o texto chama o que não foi contratado: um modo do cardápio ou um módulo do Sischef. */
const PALAVRAS_POR_TIPO = {
  modo: { noTitulo: 'Modo', naFrase: 'O modo' },
  modulo: { noTitulo: 'Módulo', naFrase: 'O módulo' },
} as const

interface ModuloNaoContratadoProps {
  /** Nome amigável do modo ou do módulo, ex.: "Totem". */
  nomeModo: string;
  /** Role do módulo a solicitar, ex.: "ROLE_CARDAPIO_TOTEM". */
  role: string;
  /** Fora das abas de modo (ex.: Cashback), passe "modulo" para o texto não dizer "Modo". */
  tipo?: keyof typeof PALAVRAS_POR_TIPO
}

/**
 * Estado exibido dentro de uma aba de modo quando a empresa NÃO contratou o
 * módulo correspondente. Explica que o modo não está habilitado e oferece o
 * botão de solicitar liberação (dispara e-mail pro comercial, via proxy do
 * backend). Espelha a tela `guard-acesso-cardapio`, mas por modo.
 */
export function ModuloNaoContratado({ nomeModo, role, tipo = 'modo' }: ModuloNaoContratadoProps): JSX.Element {
  const palavras = PALAVRAS_POR_TIPO[tipo]
  const [solicitar, status] = useAdminSolicitarAcessoMutation();
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoSolicitar(): Promise<void> {
    setErro(null);
    try {
      await solicitar({ role }).unwrap();
      setEnviado(true);
    } catch (err) {
      const msg = (err as { data?: { mensagem?: string } })?.data?.mensagem;
      setErro(msg ?? 'Não foi possível enviar a solicitação. Tente novamente.');
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
      <Stack alignItems="center" spacing={2} textAlign="center">
        <LockOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
        <Typography variant="h6">
          {palavras.noTitulo} {nomeModo} não contratado
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 460 }}>
          {palavras.naFrase} <strong>{nomeModo}</strong> não está habilitado para a sua empresa. Solicite a
          liberação abaixo — nossa equipe entrará em contato.
        </Typography>

        {enviado ? (
          <Alert severity="success" sx={{ width: '100%', maxWidth: 460, textAlign: 'left' }}>
            Solicitação enviada com sucesso. Logo entraremos em contato.
          </Alert>
        ) : (
          <>
            {erro && (
              <Alert severity="error" sx={{ width: '100%', maxWidth: 460, textAlign: 'left' }}>
                {erro}
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={aoSolicitar}
              disabled={status.isLoading}
              startIcon={status.isLoading ? <CircularProgress size={18} color="inherit" /> : <RedeemIcon />}
            >
              Solicitar acesso
            </Button>
          </>
        )}
      </Stack>
    </Paper>
  );
}
